import {useInfiniteQuery} from 'react-query'
import {useRouter} from 'next/router'
import {useState} from 'react'
import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {useAuth} from '@/products/auth/auth-provider'
import {Button} from '@/products/flexible-web-toolkit/components/button/button'
import type {ExtendedBlock, Tersed} from '@/services/kontent-ai/types'
import type {DistributorCardContentItem} from '@/products/flexible-web-toolkit/blocks/distributor-card'
import {fetchListerItems} from '@/services/lister-items/client'
import {SkeletonLoader} from '@/products/flexible-web-toolkit/components/skeleton-loader'
import {spacing} from '../../styles'
import type {ListerItemsQueryKey} from '../module-coupon-lister/types'

export type DistributorCardListerContentItem = IContentItem<{
	initialItemsNumber: Elements.NumberElement
	subsequentItemsNumber: Elements.NumberElement
	buttonText: Elements.TextElement
	paginationString: Elements.TextElement
	snippetSpacingSpacing: Elements.MultipleChoiceElement
}>

export const DistributorCardListerBlock: ExtendedBlock<
	DistributorCardListerContentItem,
	{
		block: {
			elements: {directoryItems?: Tersed<DistributorCardContentItem>[]}
		}
		totalItemCount?: number
	}
> = ({
	block,
	block: {
		elements: {
			buttonText,
			initialItemsNumber,
			subsequentItemsNumber,
			paginationString,
			snippetSpacingSpacing,
		},
	},
	BlockMapper,
	...context
}) => {
	const serverRenderedBlock = block as typeof block & {
		initialItems?: Tersed<DistributorCardContentItem>[]
		initialTotalCount?: number
	}
	const {locale = ''} = useRouter()
	const authState = useAuth()
	const [pageNumber, setPageNumber] = useState(0)
	const componentSpacing =
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- not unnecessary, snippetSpacingSpacing may be null
		spacing[snippetSpacingSpacing[0]?.codename as keyof typeof spacing] ||
		spacing.none

	const initialItemsCount = initialItemsNumber ?? 6
	const subsequentItemsCount = subsequentItemsNumber ?? 6
	const userRoles = authState.userDetails.userRoles ?? []
	const {isLoading, isFetching, data} = useInfiniteQuery({
		queryKey: [
			'distributor_card',
			{
				codename: 'distributor_card',
				locale,
				orderBy: 'order_alphabetically_by_title',
				skip: pageNumber * subsequentItemsCount,
				limit:
					pageNumber === 0 ? initialItemsCount : subsequentItemsCount,
				roles: userRoles.join(','),
			},
		] as ListerItemsQueryKey,
		queryFn: (opts) =>
			fetchListerItems<Tersed<DistributorCardContentItem>>(opts),
	})
	const displayedPages =
		data?.pages ??
		(serverRenderedBlock.initialItems
			? [{items: serverRenderedBlock.initialItems}]
			: [])
	const totalCount =
		data?.pages[0]?.pagination.totalCount ??
		serverRenderedBlock.initialTotalCount ??
		0
	const displayedItemsCount = displayedPages.flatMap(
		(page) => page.items
	).length
	const shouldShowInitialSkeleton = !data && displayedItemsCount === 0
	const shouldShowDistributorCards =
		((!isLoading && data) || displayedItemsCount > 0) &&
		displayedItemsCount > 0

	const getDisplayedItemCount = (): number => {
		return Math.min(displayedItemsCount, totalCount)
	}

	const paginationText = paginationString
		.replace('{{X}}', getDisplayedItemCount().toString())
		.replace('{{Y}}', totalCount.toString())

	const getHasNextPage = (): boolean => getDisplayedItemCount() < totalCount

	return (
		<div
			className={`mx-auto ${componentSpacing}`}
			style={{maxWidth: '1200px'}}
		>
			<p className="mb-4 text-center font-bold sm:text-left">
				{paginationText}
			</p>
			{shouldShowInitialSkeleton ? (
				<SkeletonLoader amount={initialItemsCount} />
			) : null}
			<div className="flex flex-col">
				{shouldShowDistributorCards
					? displayedPages.map((page) =>
							page.items.map((item) => (
								<div className="w-full" key={item.system.id}>
									<BlockMapper blocks={[item]} {...context} />
								</div>
							))
						)
					: null}
			</div>
			{isFetching && data ? (
				<SkeletonLoader amount={subsequentItemsCount} />
			) : null}
			{buttonText && getHasNextPage() ? (
				<div className="my-10 w-full text-center">
					<Button
						onClick={() => {
							setPageNumber((prev) => prev + 1)
						}}
						type="button"
					>
						{buttonText}
					</Button>
				</div>
			) : null}
		</div>
	)
}
